import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BUILTIN_CATEGORIES, type CategoryDef } from '@td/shared';
import { get, post, patch, del } from '../lib/api';

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['categories'] });
    void qc.invalidateQueries({ queryKey: ['events'] });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
  };
}

export function useCategories() {
  const query = useQuery({
    queryKey: ['categories'],
    queryFn: () => get<CategoryDef[]>('/categories'),
    staleTime: 5 * 60 * 1000,
  });

  const all = query.data ?? BUILTIN_CATEGORIES;
  return {
    ...query,
    /** Pickers should only offer these. */
    categories: all.filter((c) => !c.hidden),
    /** Includes hidden built-ins, for the manager and colour resolution. */
    all,
    hidden: all.filter((c) => c.hidden),
  };
}

export function useCreateCategory() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: { label: string; color: string }) =>
      post<CategoryDef>('/categories', input),
    onSuccess: invalidate,
  });
}

export function useUpdateCategory() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ key, ...input }: { key: string; label?: string; color?: string }) =>
      patch<CategoryDef>(`/categories/${key}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteCategory() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (key: string) =>
      del<{ reassigned: number; movedTo: string; canRestore: boolean }>(`/categories/${key}`),
    onSuccess: invalidate,
  });
}

export function useRestoreCategory() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (key: string) => post<CategoryDef>(`/categories/${key}/restore`),
    onSuccess: invalidate,
  });
}
